import { Bot } from 'lucide-react';
import { formatearFechaHora } from '@/lib/tareas';
import { useAsignables, useEtiquetas } from '@/hooks/useTareas';
import { useActividad } from '@/hooks/useTareasDetalle';
import type { ActividadTarea, TareaDetalle } from '@/types/tareas';

const CAMPOS: Record<string, string> = {
  titulo: 'el título',
  descripcion: 'la descripción',
  prioridad: 'la prioridad',
  fechaVencimiento: 'la fecha de vencimiento',
  consultorioId: 'el consultorio',
};

const TIPOS_RELACION: Record<string, string> = {
  paciente: 'un paciente', consulta: 'una consulta', cita: 'una cita', presupuesto: 'un presupuesto',
};

const valorCorto = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return 'vacío';
  const texto = String(v);
  // Las fechas llegan como ISO UTC; se muestran en hora local.
  return /^\d{4}-\d{2}-\d{2}T/.test(texto) ? formatearFechaHora(texto) : texto;
};

export function ActividadPanel({ tarea }: { tarea: TareaDetalle }) {
  const { data: registros = [], isLoading } = useActividad(tarea.id, true);
  const { data: usuarios = [] } = useAsignables();
  const { data: etiquetas = [] } = useEtiquetas();

  const nombreUsuario = (id: string) => {
    const u = usuarios.find(x => x.id === id);
    return u ? [u.nombre, u.apellidoPaterno].filter(Boolean).join(' ') : 'un usuario';
  };
  const nombreEtiqueta = (id: string) => etiquetas.find(e => e.id === id)?.nombre ?? 'una etiqueta';
  const lista = (ids: string[] | undefined, nombre: (id: string) => string) => (ids ?? []).map(nombre).join(', ');

  function describir(a: ActividadTarea): string {
    const d = a.detalle ?? {};
    switch (a.accion) {
      case 'creada': return 'creó la tarea';
      case 'editada': {
        const cambios = (d.cambios ?? []) as { campo: string; de?: unknown; a?: unknown }[];
        return cambios.map(c => (c.campo === 'descripcion'
          ? 'editó la descripción'
          : `cambió ${CAMPOS[c.campo] ?? c.campo} de «${valorCorto(c.de)}» a «${valorCorto(c.a)}»`)).join('; ') || 'editó la tarea';
      }
      case 'movida': return `movió la tarea de «${d.de ?? '—'}» a «${d.a ?? '—'}»`;
      case 'completada': return 'completó la tarea';
      case 'reabierta': return 'reabrió la tarea';
      case 'asignada': return `asignó a ${lista(d.usuarioIds, nombreUsuario)}`;
      case 'desasignada': return `quitó a ${lista(d.usuarioIds, nombreUsuario)}`;
      case 'etiqueta': {
        const partes: string[] = [];
        if (d.agregadas?.length) partes.push(`agregó ${lista(d.agregadas, nombreEtiqueta)}`);
        if (d.quitadas?.length) partes.push(`quitó ${lista(d.quitadas, nombreEtiqueta)}`);
        return `${partes.join(' y ')} (etiquetas)`;
      }
      case 'relacion':
        return d.accion === 'quitada'
          ? `quitó la relación con ${TIPOS_RELACION[d.tipo] ?? 'un registro'}`
          : `ligó la tarea con ${(d.relaciones ?? []).map((r: { tipo: string }) => TIPOS_RELACION[r.tipo] ?? 'un registro').join(', ')}`;
      case 'checklist':
        if (d.accion === 'creado') return `creó el checklist «${d.titulo}»`;
        if (d.accion === 'eliminado') return `eliminó el checklist «${d.titulo}»`;
        if (d.accion === 'item_completado') return `completó «${d.item}» en «${d.checklist}»`;
        if (d.accion === 'item_reabierto') return `reabrió «${d.item}» en «${d.checklist}»`;
        return 'actualizó un checklist';
      case 'comentario': return d.accion === 'eliminado' ? 'eliminó un comentario' : 'agregó un comentario';
      case 'adjunto': return d.accion === 'eliminado' ? 'eliminó un adjunto' : 'agregó un adjunto';
      case 'eliminada': return 'eliminó la tarea';
      case 'automatica': return 'creó la tarea automáticamente';
      default: return a.accion;
    }
  }

  return (
    <div className="max-w-2xl">
      {isLoading && <p className="text-sm text-text-secondary">Cargando…</p>}
      {!isLoading && registros.length === 0 && <p className="text-sm text-text-secondary">Sin actividad registrada.</p>}
      <ol className="space-y-3 border-l border-gray-200 ml-2">
        {registros.map(a => (
          <li key={a.id} className="pl-4 relative">
            <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-primary-300" />
            <p className="text-sm text-text-primary break-words">
              <span className="font-semibold">
                {a.usuarioNombre ?? <span className="inline-flex items-center gap-1"><Bot size={12} /> Sistema</span>}
              </span>{' '}
              {describir(a)}
            </p>
            <p className="text-[11px] text-text-secondary">{formatearFechaHora(a.createdAt)}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}
